import { PropsWithChildren, useState } from "react";
import { ActivityLog } from "@/types";
import { AlertCircle, Calendar, Edit2, Plus, Trash2, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DialogResponsive } from "../../dialogs";

type ShowActivityProps = PropsWithChildren & {
  log: ActivityLog;
};

export function ShowActivity({ children, log }: ShowActivityProps) {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const getEventIcon = (event: ActivityLog["event"]) => {
    switch (event) {
      case "created":
        return <Plus className="w-6 h-6 text-app-success" aria-hidden="true" />;
      case "updated":
        return <Edit2 className="w-6 h-6 text-app-warning" aria-hidden="true" />;
      case "deleted":
        return <Trash2 className="w-6 h-6 text-app-danger" aria-hidden="true" />;
      default:
        return (
          <AlertCircle className="w-6 h-6 text-app-info" aria-hidden="true" />
        );
    }
  };

  const renderValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined)
      return <span className="text-app-fg-subtle"> {""}</span>;
    if (typeof value === "object" && !Array.isArray(value)) {
      return (
        <div className="pl-4 border-l-2 border-app-border">
          {Object.entries(value).map(([key, val]) => {
            return (
              <div key={key} className="mt-2">
                {key !== "tenant_id" && key !== "password" && (
                  <>
                    <span className="font-medium">{t(key)}: </span>
                    {typeof val === "object" && val !== null ? (
                      <span>
                        {Object.values(val).map((v, i) => (
                          <span key={i}>{v}{i < Object.values(val).length - 1 ? ", " : ""}</span>
                        ))}
                      </span>
                    ) : (
                      typeof val === "string" ? t(val) : renderValue(val)
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
      );
    }
    if (Array.isArray(value)) {
      return (
        <div className="pl-4 border-l-2 border-app-border">
          {value.map((item, index) => (
            <div key={index} className="mt-2">
              {renderValue(item)}
            </div>
          ))}
        </div>
      );
    }
    return <span>{t(String(value))}</span>;
  };

  const renderChanges = (log: ActivityLog) => {
    const changes = [] as any[];

    const hasOldAndAttributes = 'old' in log.properties || 'attributes' in log.properties;

    const oldValues = hasOldAndAttributes
      ? log.properties.old || {}
      : {};

    const newValues = hasOldAndAttributes
      ? log.properties.attributes || {}
      : log.properties || {};

    for (const [key, newValue] of Object.entries(newValues)) {
      const oldValue = oldValues[key];
      const hasChanged = JSON.stringify(oldValue) !== JSON.stringify(newValue);

      changes.push(
        <div key={key} className="border-t border-app-border py-3">
          <h5 className="text-sm font-medium text-app-fg-strong mb-2">
            {t(`LABEL.${key}`)}
          </h5>

          {log.event === "updated" && hasChanged ? (
            <div className="flex flex-col sm:flex-row">
              <div className="flex-1 bg-app-danger/10 p-2 rounded-md mb-2 sm:mb-0 sm:mr-2">
                <p className="text-sm text-app-danger">
                  - {renderValue(oldValue)}
                </p>
              </div>
              <div className="flex-1 bg-app-success/10 p-2 rounded-md">
                <p className="text-sm text-app-success">
                  + {renderValue(newValue)}
                </p>
              </div>
            </div>
          ) : (
            <div
              className={`p-2 rounded-md ${log.event === "deleted" ? "bg-app-danger/10" : "bg-app-success/10"
 }`}
            >
              <p
                className={`text-sm ${log.event === "deleted" ? "text-app-danger" : "text-app-success"
 }`}
              >
                {log.event === "deleted" ? "- " : "+ "}
                {renderValue(newValue)}
              </p>
            </div>
          )}
        </div>
      );
    }

    return changes;
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("pt-BR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <DialogResponsive
      title={t("LABEL.activity logs")}
      button={children}
      defaultOpen={open}
      setDefaultOpen={setOpen}
      className="max-w-5xl 2xl:max-w-6xl w-full min-h-[95vh]"
    >
      <div className="px-6 pb-8 pt-0">
        <header>
          <div className="flex items-center gap-4 pb-6 border-b border-app-border mt-5">
            {log && getEventIcon(log.event)}
            <span className="flex flex-col justify-center items-start gap-3">
              <h5 className="text-lg leading-5 text-app-fg font-bold">
                {log.event && t(`LABEL.${log.event.toLowerCase()}`)}
              </h5>
              <p className="text-sm leading-4 text-app-fg font-normal">
                {t(log.description)}
              </p>
            </span>
          </div>
        </header>
        <section className="h-[50vh] 2xl:h-[70vh] overflow-y-auto">
          <div className="flex flex-col justify-center items-start gap-5 mt-8">
            <h5 className="text-app-fg font-bold text-lg leading-5">
              Detalhes
            </h5>
            <span className="flex justify-start items-center w-full gap-80">
              <p className="font-normal text-sm text-app-fg-muted leading-4">
                Modulo:
              </p>
              <p className="font-[500] text-sm text-app-fg leading-4">
                {t(`LABEL.${log.subject_type.toLocaleLowerCase()}`)}
              </p>
            </span>
          </div>
          <div className="flex flex-col justify-center items-start gap-5 mt-8">
            <h5 className="text-app-fg font-bold text-lg leading-5">
              Usuario Responsavel
            </h5>
            <span className="flex items-center w-full gap-3 pb-5">
              <User className="text-2xl text-app-fg-subtle" />
              <p className="font-normal text-sm text-app-fg-muted leading-4 mb-0">
                {log.causer.name}
              </p>
            </span>
            <span className="flex justify-start items-center w-full gap-80">
              <div className="flex flex-col gap-3">
                <p className="font-normal text-sm text-app-fg-muted leading-4">
                  Email:
                </p>
                <p className="font-normal text-sm text-app-fg-muted leading-4">
                  Função:
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <p className="font-medium text-sm text-app-fg leading-4">
                  {log?.causer.email}
                </p>
                <p className="font-medium text-sm text-app-fg leading-4">
                  {log?.causer.role}
                </p>
              </div>
            </span>
          </div>
          {log && (
            <>
              {/* {log.event === 'created' && <CreateLogs log={log} />}

                {log.event === 'updated' && <UpdateLogs log={log} />}

                {log.event === 'deleted' && <DeleteLogs log={log} />} */}
              <div className="space-y-2">
                <h4 className="text-md font-semibold mb-4 mt-8">
                  {log.event === "created"
                    ? "Dados Criados"
                    : log.event === "updated"
                      ? "Alterações"
                      : "Dados Excluídos"}
                </h4>
                <div className="border border-app-border rounded-md p-4">
                  {renderChanges(log)}
                </div>
              </div>
            </>
          )}
        </section>
        <div className="flex items-center space-x-2 text-sm text-app-fg-muted pt-5 pb-3">
          <Calendar className="w-4 h-4" aria-hidden="true" />
          <span>{log && formatDate(log.created_at)}</span>
        </div>
      </div>
    </DialogResponsive>
  );
}
